import React, { useCallback, useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import SafetyNotice from '../SafetyNotice';
import HPToolsInFlight from './HPToolsInFlight';
import PlaceKeepingCounter from './PlaceKeepingCounter';
import ThreeWayCommLog from './ThreeWayCommLog';
import HoldPointsSection from './HoldPointsSection';
import AttachmentsSection from './AttachmentsSection';
import ConditionsChangedBanner from './ConditionsChangedBanner';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * PerformPhase — live-work board. Composed sections:
 *   • ConditionsChangedBanner (STOP trigger + pending state overlay)
 *   • HoldPointsSection       (active holds — BLOCKS PK advancement)
 *   • HPToolsInFlight         (check-off + counter)
 *   • PlaceKeepingCounter     (step-by-step tracker)
 *   • ThreeWayCommLog         (append-only)
 *   • AttachmentsSection      (photos + files)
 *
 * A pending `conditions_changed` event blocks EVERY perform tool below
 * the banner. The active-holds count is passed to PlaceKeepingCounter so
 * step advancement stays disabled while any hold is open.
 */
export default function PerformPhase({ draft, onReload }) {
  const draftId = draft?.id;
  const ownerEmail = draft?.owner_email;
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!draftId) return;
    try {
      const s = await jobBriefApi.getPerformState(draftId);
      setState(s);
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleLog = useCallback(async (eventType, payload) => {
    if (!draftId || !ownerEmail) return;
    await jobBriefApi.logPerformEvent(draftId, ownerEmail, eventType, payload);
    await refresh();
  }, [draftId, ownerEmail, refresh]);

  const conditionsPending = !!state?.conditions_changed_pending;
  const activeHoldsCount = state?.counts?.active_holds ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="jbv2-perform-phase">
      <SafetyNotice />

      <ConditionsChangedBanner
        draftId={draftId}
        performState={state}
        ownerEmail={ownerEmail}
        onFlagged={async () => {
          await refresh();
          // Bubble up so the parent editor reloads the draft (phase went back to plan).
          onReload?.();
        }}
      />

      {/* Compact activity strip */}
      <div
        style={{
          padding: '10px 14px', borderRadius: 12,
          background: 'rgba(15,27,70,0.65)',
          border: '1px solid rgba(103,232,249,0.28)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}
        data-testid="jbv2-perform-activity-strip"
      >
        <Activity size={16} color="#67e8f9" />
        <span style={{ color: '#94a3b8', fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 800 }}>
          Live activity
        </span>
        <span className="jbv2-hp-row-meta">
          HP uses: <strong data-testid="jbv2-perform-count-hp">{state?.counts?.hp_tool_uses ?? 0}</strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Steps: <strong data-testid="jbv2-perform-count-steps">{state?.counts?.place_keeping_steps ?? 0}</strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Comms: <strong data-testid="jbv2-perform-count-comms">{state?.counts?.three_way_comms ?? 0}</strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Holds: <strong data-testid="jbv2-perform-count-holds">{activeHoldsCount}</strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Files: <strong data-testid="jbv2-perform-count-attachments">{state?.counts?.attachments ?? 0}</strong>
        </span>
        {loading && <span className="jbv2-hp-row-meta" style={{ marginLeft: 'auto' }}>Loading…</span>}
      </div>

      {/* Blocked overlay when conditions have changed and rebrief is pending */}
      <div
        style={{ opacity: conditionsPending ? 0.55 : 1, pointerEvents: conditionsPending ? 'none' : 'auto', display: 'contents' }}
        data-testid="jbv2-perform-body"
        aria-disabled={conditionsPending}
      >
        <HoldPointsSection
          draftId={draftId}
          performState={state}
          ownerEmail={ownerEmail}
          onChanged={refresh}
          isReadOnly={conditionsPending}
        />
        <HPToolsInFlight
          draft={draft}
          performState={state}
          ownerEmail={ownerEmail}
          onLogged={handleLog}
          isReadOnly={conditionsPending}
        />
        <PlaceKeepingCounter
          performState={state}
          onLogged={handleLog}
          isReadOnly={conditionsPending}
          activeHoldsCount={activeHoldsCount}
        />
        <ThreeWayCommLog
          performState={state}
          ownerEmail={ownerEmail}
          onLogged={handleLog}
          isReadOnly={conditionsPending}
        />
        <AttachmentsSection
          draftId={draftId}
          performState={state}
          ownerEmail={ownerEmail}
          onChanged={refresh}
          isReadOnly={conditionsPending}
        />
      </div>
    </div>
  );
}
